import axios from 'axios';
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBChip,
  MDBCol,
  MDBContainer,
  MDBDatepicker,
  MDBIcon,
  MDBInput,
  MDBInputGroup,
  MDBRipple,
  MDBRow,
  MDBSpinner,
  MDBTypography,
  MDBValidationItem
} from 'mdb-react-ui-kit';
import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import venmo from './../../venmo.png';
import zelle from './../../zelle.png';
import EmptyCart from './EmptyCart';
import { fetchMainPageData } from '../utils/Common';
import { CartContext } from '../context/CartProvider';
import { NavbarContext } from '../context/NavProvider';
import { UserProfileContext } from '../context/UserProfileProvider';
import { GeographyContext } from '../context/GeographyProvider';
import { CategoryContext } from '../context/CategoryContext';

function Cart() {
  const {selectCategories,categoryId} = useContext(CategoryContext);
  const {tokenLoaded} = useContext(UserProfileContext);
  const {ipAddress,location}= useContext(GeographyContext);
  const { isPricing,setIsPricing,pageData,setPageData,setBusiness} = useContext(NavbarContext);
  const { qty, setQty, setCartCount, cartId, setCartId, setCart, setAddedEntities, weight, setWeight,setOrders ,setPaymentInfo} = useContext(CartContext);
  const didFetchRef = useRef(false);
  const nextWeek = new Date();

  const [deliveryDate, setDeliveryDate] = useState(nextWeek.setDate(nextWeek.getDate() + 9));

  
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPrice, setTotalPrice] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [grandTotal, setGrandTotal] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [totalWt, setTotalWt] = useState(null);
  const [totalQty, setTotalQty] = useState(null);
  const [totalSalesTax, setTotalSalesTax] = useState(null);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_INVENTORY_URL;
  const pricingCategoryId = selectCategories && selectCategories.length > 0 && selectCategories[0]?.text;

  useEffect(() => {
    if (!pageData) {
      fetchMainPageData( setPageData, setIsPricing,pageData,setBusiness);
    }
  }, []);

  useEffect(() => {
    setIsPricing(window.location.host.includes("expenses") || window.location.host.includes("x-trk"));
    !cartId && setCart(null);
    !cartId && setCartCount(0) && setAddedEntities([]);
    async function fetchPaymentInfo() {
      try {
        const response = await axios.get(`${baseUrl}/paymentinfo/651f7b78a4c5ba72dde1e446`);
        setPaymentInfo(response.data);
      } catch (error) {
        console.error('Error fetching payment info:', error);
      }
    }
    fetchPaymentInfo();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const cartInStorage = localStorage.getItem('CART-ID');
      const ipAddressInStorage = localStorage.getItem('ipAddress');
  
      if (!cartId && !tokenLoaded && !cartInStorage) {
        navigate('/empty-cart');
        return;
      }
  
      if (!ipAddress && !ipAddressInStorage) {
        return;
      }
  
      const getIPAddress = () => {
        return ipAddress || ipAddressInStorage;
      }
  
      const cartUrl = tokenLoaded
        ? `${baseUrl}/cart`
        : `${baseUrl}/cart/anonymous/${cartId || cartInStorage}?ipAddress=${getIPAddress()}`;
  
      let cartResponse;
      try {
        cartResponse = await axios.get(cartUrl);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setCartCount(0);
          setCart(null);
          setQty({});
          setWeight({});
          setAddedEntities([]);
          navigate('/empty-cart');
          return;
        } else {
          throw error;
        }
      }
      const cartForId = cartResponse.data;
      if (cartForId) {
        const count = cartForId.totalQty;
        setCartCount(count);
        setCartId(cartForId.id);
      } else {
        setCartCount(0);
        setCart(null);
        setQty({});
        setWeight({});
        setAddedEntities([]);
      }
  
      const url = tokenLoaded
        ? `${baseUrl}/cart/cartSummary`
        : `${baseUrl}/cart/cartSummary/anonymous/${cartId || cartInStorage}`;
  
      const latLngQuery = location
        ? `?latitude=${location.latitude}&longitude=${location.longitude}`
        : `?ipAddress=${getIPAddress()}`;
  
      const summaryUrl = url + latLngQuery;
      const response = await axios.get(summaryUrl);
  
      const cart = response.data.entities;
      const newAddedEntities = new Set(response.data.entities?.map(entity => entity.id) || Object.keys(response.data.entityIdToQty || {}));
      const count = response.data?.totalQty;
  
      setAddedEntities(newAddedEntities);
      setCartCount(count);
      setCart(response.data);
      setTotalPrice(response.data.totalAmount);
      setGrandTotal(response.data.grandTotal);
      setCurrencySymbol(response.data.currencySymbol);
      setTotalSalesTax(response.data.totalSalesTax);
      setTotalWt(response.data.totalWeight);
      setTotalQty(response.data.totalQty);
      setMetrics(response.data.metrics);
      setCartItems(cart);
  
      if (cart && cart.entityIdToQty) {
        const updatedQty = {};
        const updatedWeight = {};
  
        Object.keys(cart.entityIdToQty).forEach(entityId => {
          updatedQty[entityId] = cart.entityIdToQty[entityId].qty;
          updatedWeight[entityId] = cart.entityIdToQty[entityId].weight;
        });
  
        setQty(updatedQty);
        setWeight(updatedWeight);
  
        setCartCount(response.data.totalQty);
      }
    } catch (error) {
      console.error("Error fetching cart data:", error);
    } finally {
      setLoading(false);
    }
  }, [cartId, tokenLoaded, baseUrl, location, ipAddress, navigate, setAddedEntities, setCart, setCartCount, setCartId, setQty, setWeight]);
  
  useEffect(() => {
    if (cartId !== undefined || tokenLoaded || localStorage.getItem('CART-ID')) {
      fetchData();
      didFetchRef.current = true;
    }
  }, []);

  const removeCartItem = async (item) => {
    try {
      const id = cartId;
      let url = item ? (tokenLoaded ? `${baseUrl}/cart/${id}/entity/${item.id}` : `${baseUrl}/cart/anonymous/${id}/entity/${item.id}`) : `${baseUrl}/cart/${cartId}`;

      let latLngQuery = "";
      if (location) {
        console.log(`Latitude: ${location.latitude}, Longitude: ${location.longitude}`);
        latLngQuery = `?latitude=${location.latitude}&longitude=${location.longitude}`;
      } else {
        latLngQuery = `?ipAddress=${ipAddress}`;
      }
      url = url + latLngQuery;

      const response = await axios.delete(url);
      if (response.status === 200) {
        const cartRes = response.data.entities;
        if (cartRes) {
          setCart(response.data);
          setTotalPrice(response.data.totalAmount);
          setGrandTotal(response.data.grandTotal);
          setTotalSalesTax(response.data.totalSalesTax);
          setTotalWt(response.data.totalWeight);
          setTotalQty(response.data.totalQty);
          setCartItems(cartRes);
          setCartCount(cartRes.length);
          setAddedEntities(cartRes);
        } else {
          setAddedEntities([]);
          setCartId(null);
          setCartCount(0);
          localStorage.removeItem('CART-ID');
          setCartItems([]);
          (isPricing || localStorage.getItem('isPricing')) ? navigate("/shop/pricing") : navigate("/products");
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setAddedEntities([]);
        setCartId(null);
        setCartCount(0);
        setCartItems([]);
        navigate("/products");
      } else {
        console.error("Error removing item", error);
      }
    }
  };

  const updateCart = async (item, newQty, newWeight) => {
    try {
      const cart = {
        entityId: item.id,
        entityIdToQty: {
          [item.id]: {
            qty: newQty,
            weight: newWeight,
            metrics: item.metrics || 'lbs'
          }
        }
      };

      let url = tokenLoaded ? `${baseUrl}/cart/${cartId}` : `${baseUrl}/cart/anonymous/${cartId}`;
      let latLngQuery = location ? `?latitude=${location.latitude}&longitude=${location.longitude}` : `?ipAddress=${ipAddress}`;
      url = url + latLngQuery;

      let response = await axios.put(url, cart);
      const cartRes = response.data.entities;

      setCart(response.data);
      setTotalPrice(response.data.totalAmount);
      setGrandTotal(response.data.grandTotal);
      setTotalSalesTax(response.data.totalSalesTax);
      setTotalWt(response.data.totalWeight);
      setTotalQty(response.data.totalQty);
      setCartItems(cartRes);
      setCartCount(response.data.totalQty);
    } catch (error) {
      console.error('Error creating or updating cart:', error);
    }
  };

  const handleQtyChange = (item, newQty) => {
    setQty(prev => ({ ...prev, [item.id]: newQty }));
    updateCart(item, newQty, weight[item.id] || item.weight || 1);
  };

  const handleWeightChange = (item, newWeight) => {
    setWeight(prev => ({ ...prev, [item.id]: newWeight }));
    updateCart(item, qty[item.id] || item.qty || 1, newWeight);
  };

  const linkProps = pricingCategoryId ? 
  {
    pathname: `/checkout/${pricingCategoryId}`
  } : 
  {
    pathname: '/checkout'
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        navigate('/products');
      }
    }, 10000); // 10 seconds

    return () => clearTimeout(timer); // Cleanup the timer if the component unmounts or loading changes
  }, [loading, navigate]);

  return (
    <section className="mb-2 mt-2 position-relative grainy-gradient-intro justify-content-center text-center">
      {loading ? (
        <div className='d-flex m-5 justify-content-center'>
          <MDBSpinner style={{ width: '6rem', height: '6rem' }}>
            <span className='visually-hidden'>Loading...</span>
          </MDBSpinner>
        </div>
      ) : (
        cartItems?.length > 0 ? (
          <MDBContainer className="mb-2 position-relative grainy-gradient-intro justify-content-center text-center">
            {isPricing && (
              <span className='justify-content-center align-items-center' style={{ minWidth: '10rem', textAlign: 'center' }}>
                <Link to='/' className='m-1' href='#'>
                  <MDBIcon fas icon="hand-point-left" /> Go Back to Subscribe
                </Link>
              </span>
            )}
            <MDBRow className="d-flex justify-content-center">
              <MDBCol lg="8" className="mb-2">
                <section className="mb-2">
                  {cartItems?.map((item, index) => (
                    <MDBCard className="shadow-4 p-4 rounded-5 mb-2" key={index}>
                      <MDBCardBody>
                        <MDBRow className="border-bottom mb-1 ">
                          {isPricing && (
                            <MDBCardHeader className="d-flex justify-content-center align-items-center mb-5">
                              <img
                                src={
                                  Array.isArray(item?.thumbnails) && Array.isArray(item?.imageIds) && item.imageIds.length > 0 
                                    ? item.thumbnails[item.imageIds.length - 1] // Access the last image
                                    : 'fallback-image-url' // Fallback in case there are no images
                                }
                                alt=""
                                style={{ width: '80px', height: '80px' }}
                                className='rounded'
                              />
                              <h4
                                className="option"
                                style={{
                                  padding: "1.5rem",
                                  marginBottom: 0,
                                  fontWeight: 500
                                }}
                              >
                                {item?.name}
                              </h4>
                            </MDBCardHeader>
                          )}
                          {isPricing ? (
                            <MDBCol md="6" className="mb-2 justify-content-center align-items-center">
                              <MDBCardBody className="text-dark justify-content-center align-items-center">
                                {item?.features?.map((feature, featureIndex) => (
                                  <p key={featureIndex} style={{ textAlign: 'left', display: 'flex' }}>
                                    <MDBIcon fas icon={feature?.icon} className="me-4" />
                                    {feature?.textValue}
                                  </p>
                                ))}
                              </MDBCardBody>
                            </MDBCol>
                          ) : (
                            <MDBCol md="2" className="mb-2">
                              <MDBRipple rippleTag='a'>
                                <img
                                  src={item && item.thumbnails && item.thumbnails[0].replace('_thumbnail', '')}
                                  className='img-fluid rounded'
                                  alt='example'
                                />
                              </MDBRipple>
                            </MDBCol>
                          )}
                          {!isPricing  && (
                            <MDBCol md="4" className="mb-2 justify-content-center">
                              <p className="fw-bold text-center">{item.name}</p>
                              <p className="mb-1 text-center">
                                <MDBCol className="mb-2">
                                  <MDBBadge pill color='primary' light>
                                    Weight: {item.weight}<span className="text-muted fw-bold fs-6 me-2 ms-2">{item.metrics}</span>
                                  </MDBBadge>
                                </MDBCol>
                                <MDBCol className="mb-2">
                                  <MDBBadge pill color='primary' light>
                                    Price: {item.currencySymbol}{item.originalPrice}/{item.metrics.replace('s', '')}
                                  </MDBBadge>
                                </MDBCol>
                                <MDBCol className="mb-2">
                                  <MDBBadge pill color='primary' light>
                                    {item.totalWeightInKgs} or {item.totalWeightInLbs}
                                  </MDBBadge>
                                </MDBCol>
                                {parseInt(item.discountedPrice, 10) > 0 && (
                                  <MDBCol className="mb-5">
                                    <MDBChip size='sm'>Discounted Price: {item.currencySymbol}{item.discountedPrice}/{item.metrics}</MDBChip>
                                  </MDBCol>
                                )}
                              </p>
                            </MDBCol>
                          )}
                          <MDBCol xs='6' sm='6' md='6' className='justify-content-center align-items-center d-flex'>
                            <MDBRow>
                              {!isPricing  && (
                                <MDBInputGroup>
                                  <MDBCol xs='6' sm='6' md='6' className='me-1 d-flex align-items-center text-center'>
                                    <MDBInput label="Quantity" type="number" id={`qty-${item.id}`} className="form-control text-center" value={qty[item.id] || item.qty || 1}   min="1" step="1"  onChange={e => handleQtyChange(item, parseFloat(e.target.value))}/>
                                    <div className="d-flex flex-column align-items-center me-1">
                                      <MDBBtn color="link" size="sm" onClick={() => handleQtyChange(item, (qty[item.id] || item.qty || 1) + 1)}>+</MDBBtn>
                                      <MDBBtn color="link" size="sm" onClick={() => handleQtyChange(item, Math.max((qty[item.id] || item.qty || 1) - 1, 1))}>-</MDBBtn>
                                    </div>
                                  </MDBCol>
                                  <MDBCol xs='6' sm='6' md='6' className='me-1 d-flex align-items-center justify-content-center'>
                                    <MDBInput label='Wt'  name='Weight'  id={`weight-${item.id}`} type='number'  min="0.25"  step={0.25}  value={weight[item.id] || item.weight || 1} onChange={e => handleWeightChange(item, parseFloat(e.target.value))}/>
                                    <div className="d-flex flex-column align-items-center">
                                      <MDBBtn className='rounded' color="link" size="sm" onClick={() => handleWeightChange(item, Math.max((weight[item.id] || item.weight || 1) + 0.25, 0.25))}>+</MDBBtn>
                                      <MDBBtn color="link" size="sm" onClick={() => handleWeightChange(item, Math.max((weight[item.id] || item.weight || 1) - 0.25, 0.25))}>-</MDBBtn>
                                    </div>
                                  </MDBCol>
                                </MDBInputGroup>
                              )}
                            </MDBRow>
                            <figure className='mb-0 ms-3'>
                              <MDBTypography blockquote>
                                <h5 className="mb-2 text-center">{currencySymbol}{item.totalPrice || 0.0}</h5>
                              </MDBTypography>
                              {item.saved > 0 && (
                                <figcaption className='blockquote-footer mb-0'>
                                  Saved <cite title='Source Title'>{currencySymbol}{item.saved || 0.0}</cite>
                                </figcaption>
                              )}
                            </figure>
                          </MDBCol>
                        </MDBRow>
                        <Link to={`/shop/${categoryId}`}>
                          <MDBBtn color='link' className='mt-2'>Back to Shop</MDBBtn>
                        </Link>
                        <span>
                          <MDBBtn color='link' className="text-muted" onClick={() => removeCartItem(item)}>
                            <small>
                              <MDBIcon icon="trash" className="me-1" /> Remove
                            </small>
                          </MDBBtn>
                        </span>
                      </MDBCardBody>
                    </MDBCard>
                  ))}
                </section>
                {/* ... other sections ... */}
              </MDBCol>
              <MDBCol lg="4" className="mb-4">
                <MDBCard className="shadow-4 p-1 rounded-5 mb-2">
                  <MDBCardBody>
                    <h5 className="mb-5">Summary</h5>
                    {!isPricing && (
                      <div className="d-flex justify-content-between mb-3">
                        <span>Total Qty</span>
                        <span>{totalQty}</span>
                      </div>
                    )}
                    {!isPricing && (
                      <div className="d-flex justify-content-between mb-3">
                        <span>Total Wt</span>
                        <span>{totalWt} {metrics}</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between mb-3">
                      <span>Total Item Cost</span>
                      <span>{currencySymbol}{totalPrice}</span>
                    </div>
                    {!isPricing && (
                      <div className="d-flex justify-content-between mb-3">
                        <span>Shipping Cost</span>
                        <span>{currencySymbol}{' Included'}</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between">
                      <span>TAX </span>
                      <span>{currencySymbol}{totalSalesTax}</span>
                    </div>
                    <hr className="my-4" />
                    <div className="d-flex justify-content-between fw-bold mb-5">
                      <span className="d-inline-block">Grand Total </span>
                      <span>{currencySymbol}{grandTotal}</span>
                    </div>
                    <Link to={linkProps} onClick={() => setOrders(undefined)}>
                      <MDBBtn className="d-flex justify-content-center" color="primary" rounded block>
                        Proceed to CHECKOUT
                      </MDBBtn>
                    </Link>
                  </MDBCardBody>
                </MDBCard>
                <MDBCard className="shadow-4 p-4 rounded-5">
                  <MDBCardHeader>
                    <MDBTypography variant="h6" className="mb-2 text-success mt-3">We accept</MDBTypography>
                  </MDBCardHeader>
                  <MDBCardBody>
                    <section className='justify-content-center align-items-center'>
                      {!isPricing && (
                        <MDBRow className='justify-content-center'>
                          <MDBCol className='align-items-center'>
                            <MDBTypography variant="h5" className="mb-2 text-warning">Expected delivery</MDBTypography>
                            <MDBTypography className='text-info'>7 working days</MDBTypography>
                            <span className="text-muted small align-middle mb-1">or</span>
                          </MDBCol>
                          <MDBValidationItem className='fw-bold'>
                          <MDBDatepicker   format='dd, dddd, mmmm, yyyy'  min={nextWeek}  disablePast  onChange={(date) => setDeliveryDate(date)} inputLabel='Select a delivery date'  />
                          </MDBValidationItem>
                        </MDBRow>
                      )}
                      <img className="mr-2 mt-2" width="45px" src="https://mdbootstrap.com/wp-content/plugins/woocommerce-gateway-stripe/assets/images/visa.svg" alt="Visa" style={{ marginRight: "20px" }} />
                      <img className="mr-2 mt-2" width="45px" src="https://mdbootstrap.com/wp-content/plugins/woocommerce-gateway-stripe/assets/images/amex.svg" alt="American Express" style={{ marginRight: "20px" }} />
                      <img className="mr-2 mt-2" width="45px" src="https://mdbootstrap.com/wp-content/plugins/woocommerce-gateway-stripe/assets/images/mastercard.svg" alt="Mastercard" style={{ marginRight: "20px" }} />
                      <img className="mr-2 mt-2" width="45px" src="https://mdbootstrap.com/wp-content/plugins/woocommerce-gateway-stripe/assets/images/discover.svg" alt="Mastercard" style={{ marginRight: "20px" }} />
                      {!isPricing  && (
                        <>
                          <img className="mr-2 mt-2 rounded" width="45px" src="https://mdbootstrap.com/wp-content/plugins/woocommerce/includes/gateways/paypal/assets/images/paypal.png" alt="PayPal acceptance mark" style={{ marginRight: "20px" }} />
                          <img className="mr-2 mt-2 rounded" width="45px" height='20px' src={venmo} alt="Venmo" style={{ marginRight: "20px" }} />
                          <img className="mr-2 mt-2 rounded" width="45px" height='20px' src={zelle} alt="zelle" style={{ marginRight: "20px" }} />
                        </>
                      )}
                    </section>
                  </MDBCardBody>
                </MDBCard>
              </MDBCol>
            </MDBRow>
          </MDBContainer>
        ) : (
          <EmptyCart />
        )
      )}
    </section>
  );
}

export default Cart;
